import type {
  CommandExecuteInput,
  CommandExecuteOutput,
  CommandStepInput,
  CommandStepOutput,
} from '@travel-planner/shared/schemas';
import type { AgentPanelAdapter } from './types';
import { useCallback, useEffect } from 'react';
import { handleNavigateHandoff } from './handle-navigate-handoff';
import { handlePageActionResult } from './handle-page-action';
import { useAgentPanelStore } from './use-agent-panel-store';

export type CommandStepMutate = (
  input: CommandStepInput
) => Promise<CommandStepOutput>;

export type CommandExecuteMutate = (
  input: CommandExecuteInput
) => Promise<CommandExecuteOutput>;

export type UseAgentCommandArgs = {
  adapter: AgentPanelAdapter | null | undefined;
  stepMutate: CommandStepMutate;
  executeMutate: CommandExecuteMutate;
  // Optional workflow integration. Pass `useAgentWorkflow().startWorkflow`
  // here to enable LLM-routed workflow entry: when the planner returns
  // `phase: 'workflow-requested'`, the hook auto-calls
  // `startWorkflow(name)`. Step-to-step transitions are NOT auto-fired —
  // they happen when the consultant clicks "Continue" in the panel (see
  // `command-session-panel.tsx`). When omitted, workflows are inert and
  // the command hook behaves exactly as before.
  workflow?: {
    startWorkflow: (
      name: string,
      seed?: Record<string, unknown>
    ) => Promise<void>;
  };
};

export type UseAgentCommandReturn = {
  startSession: (utterance: string) => Promise<void>;
  sendTurn: (utterance: string) => Promise<void>;
  confirmAndExecute: () => Promise<void>;
  cancel: () => void;
};

const buildStepInput = (
  adapter: AgentPanelAdapter,
  utterance: string,
  snapshot: {
    command: string | null;
    collected: Record<string, unknown>;
    history: Array<{ role: 'user' | 'assistant'; content: string }>;
  }
): CommandStepInput =>
  ({
    contextType: adapter.contextType,
    ...adapter.context,
    utterance,
    snapshot,
  }) as CommandStepInput;

const buildExecuteInput = (
  adapter: AgentPanelAdapter,
  command: string,
  params: Record<string, unknown>
): CommandExecuteInput =>
  ({
    contextType: adapter.contextType,
    ...adapter.context,
    command,
    params,
  }) as CommandExecuteInput;

// Server errors can include database / SQL internals (e.g. "Failed query:
// select count(*) from ..."). Surfacing those raw inside the command-session
// chat bubble leaks implementation details and confuses the consultant. Strip
// known internal prefixes and fall back to a generic phrase. The full error
// is still visible in the network panel + server logs, so debuggability is
// unchanged.
const SQL_ERROR_PATTERNS = [
  /^Failed query:/i,
  /\bsyntax error\b/i,
  /\bduplicate key\b/i,
  /\binvalid input syntax\b/i,
  /\brelation .* does not exist\b/i,
  /^[a-z_]+:.*postgres/i,
];

// Exported for testing — see use-agent-command.test.ts.
export const sanitiseCommandError = (raw: string): string => {
  if (SQL_ERROR_PATTERNS.some((re) => re.test(raw))) {
    return 'Something went wrong running that — try rephrasing the query.';
  }
  return raw;
};

export const useAgentCommand = ({
  adapter,
  stepMutate,
  executeMutate,
  workflow,
}: UseAgentCommandArgs): UseAgentCommandReturn => {
  const startCommandSession = useAgentPanelStore((s) => s.startCommandSession);
  const appendUserTurn = useAgentPanelStore((s) => s.appendUserTurn);
  const applyServerStep = useAgentPanelStore((s) => s.applyServerStep);
  const markExecuting = useAgentPanelStore((s) => s.markExecuting);
  const setCommandError = useAgentPanelStore((s) => s.setCommandError);
  const cancelCommand = useAgentPanelStore((s) => s.cancelCommand);
  const closePanel = useAgentPanelStore((s) => s.close);

  // After every server step, check for workflow auto-routing:
  //  - 'workflow-requested' → fire `startWorkflow(name)`
  // We do NOT auto-advance to the next workflow step when a step finishes:
  // the consultant needs a chance to review the result (e.g. the product
  // list from `find_product`) and explicitly click "Continue" — otherwise
  // the result panel disappears as soon as it appears. The Continue click
  // is wired up in `command-session-panel.tsx` and fires
  // `workflow.advanceFromStepResult(session.result)` directly.
  //
  // When the command lands in `phase: 'done'` we delegate to
  // `handlePageActionResult`, which fires the adapter's `onCommandResult`
  // for every result and — when the result is a page-action kind
  // (apply_filters etc.) — also auto-closes the panel, flashes the page
  // root, and surfaces an Undo toast that restores the snapshot URL.
  // Renderer registry still runs, so non-page-action kinds (e.g.
  // operation_list) keep their in-panel rendering. See
  // `.claude/rules/agent-panel-page-actions.md`.
  const handleServerStep = useCallback(
    async (out: CommandStepOutput) => {
      applyServerStep(out);
      if (out.phase === 'done') {
        handlePageActionResult({
          result: out.result,
          adapter,
          closePanel,
        });
      }
      if (out.phase === 'navigate') {
        // Cross-page handoff: planner detected the consultant's intent
        // belongs on a different page. The store is already in
        // `pendingNavigation` (set by applyServerStep); the handler stashes
        // the proposed target so the panel can render the confirm card.
        // Confirmation drives the actual navigate + replay (see
        // NavigateHandoffCard). No workflow integration needed.
        handleNavigateHandoff({
          target: out.target,
          reason: out.reason,
          replayUtterance: out.replayUtterance,
        });
        return;
      }
      if (!workflow) return;
      if (out.phase === 'workflow-requested' && out.workflow) {
        await workflow.startWorkflow(out.workflow, out.seed);
        return;
      }
    },
    [adapter, applyServerStep, closePanel, workflow]
  );

  const sendTurn = useCallback(
    async (utterance: string) => {
      if (!adapter) return;
      const trimmed = utterance.trim();
      if (!trimmed) return;
      const session = useAgentPanelStore.getState().commandSession;
      const snapshot = {
        command: session?.command ?? null,
        collected: session?.collected ?? {},
        history: session?.history ?? [],
      };
      try {
        const out = await stepMutate(
          buildStepInput(adapter, trimmed, snapshot)
        );
        await handleServerStep(out);
      } catch (err) {
        setCommandError(
          sanitiseCommandError(
            err instanceof Error ? err.message : 'Request failed'
          )
        );
      }
    },
    [adapter, stepMutate, handleServerStep, setCommandError]
  );

  const startSession = useCallback(
    async (utterance: string) => {
      if (!adapter) return;
      const trimmed = utterance.trim();
      if (!trimmed) return;
      startCommandSession(trimmed);
      // After startCommandSession, the store's commandSession.history is
      // [...priorHistory, { role: 'user', content: trimmed }]. Send THAT
      // to the planner so the LLM sees prior turns and can interpret
      // refinement prompts ("be more precise to Lisbon") in the context
      // of the previous result, instead of treating each ask as a
      // standalone command and asking what category to search.
      // `command`/`collected` stay empty — this is a new ask, not a
      // continuation of param-collection for an in-flight command.
      const seeded = useAgentPanelStore.getState().commandSession;
      const history = seeded?.history ?? [
        { role: 'user' as const, content: trimmed },
      ];
      try {
        const out = await stepMutate(
          buildStepInput(adapter, trimmed, {
            command: null,
            collected: {},
            history,
          })
        );
        await handleServerStep(out);
      } catch (err) {
        setCommandError(
          sanitiseCommandError(
            err instanceof Error ? err.message : 'Request failed'
          )
        );
      }
    },
    [
      adapter,
      stepMutate,
      startCommandSession,
      handleServerStep,
      setCommandError,
    ]
  );

  const confirmAndExecute = useCallback(async () => {
    if (!adapter) return;
    const session = useAgentPanelStore.getState().commandSession;
    if (!session?.command || session.phase !== 'confirm') return;
    markExecuting();
    try {
      const out = await executeMutate(
        buildExecuteInput(adapter, session.command, session.collected)
      );
      // commandExecute output is always { phase: 'done', ... } — narrow it
      // by passing through the same handler so the workflow auto-advance
      // also fires here.
      await handleServerStep(out);
    } catch (err) {
      setCommandError(
        sanitiseCommandError(
          err instanceof Error ? err.message : 'Execution failed'
        )
      );
    }
  }, [
    adapter,
    executeMutate,
    markExecuting,
    handleServerStep,
    setCommandError,
  ]);

  const cancel = useCallback(() => {
    cancelCommand();
  }, [cancelCommand]);

  // Replay-on-mount for cross-page handoff. When the consultant confirms
  // a `request_navigation` proposal, the panel sets `pendingHandoffReplay`
  // and navigates. Once the new page mounts its adapter (different
  // contextType, same singleton store), this effect consumes the slot and
  // fires `startSession` with the original utterance — the LLM then sees
  // the right tool catalog and can run the command. The store's consumer
  // is atomic so React 19 StrictMode's double-mount can't replay twice.
  useEffect(() => {
    if (!adapter) return;
    const replay = useAgentPanelStore
      .getState()
      .consumePendingHandoffReplay(adapter.contextType);
    if (replay) void startSession(replay);
  }, [adapter, startSession]);

  return {
    startSession,
    sendTurn: async (utterance: string) => {
      appendUserTurn(utterance);
      await sendTurn(utterance);
    },
    confirmAndExecute,
    cancel,
  };
};
